import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CircularProgress from "@mui/material/CircularProgress";
import Backdrop from '@mui/material/Backdrop';
import { useTheme } from '@mui/material/styles';
import {
  EmptyState,
  EmptyTitle,
  EmptySubtitle,
  NewChatButton,
  ChatMain,
  SidebarHandle,
} from "./styled";
import { getChatsForUser, createChat, renameChat } from "../api/chat";
import { mapApiChat } from "../utils/transform";
import ChatSidebar from "./ChatSidebar";
import ChatMainView from "./ChatMainView";
import { useChatMessages, MessagesMap } from "../hooks/useChatMessages";
import { useUser } from "../components/UserContext";
import { ChatSummary, DatabaseInfo } from "../types/chat";
import { clearBrowserCache } from "../utils/clearCache"; // <-- Импортируем функцию очистки

const DBS: DatabaseInfo[] = [
  { id: "wt_mouldplast", name: "Wt_Mouldplast" },
  { id: "self", name: "Self" },
  { id: "wpdemo1", name: "Workplan" },
];

const Chat: React.FC = () => {
  const { userId, username, fullName, Email } = useUser();
  const navigate = useNavigate();
  const theme = useTheme();
  const location = useLocation();
  const { chatId: paramChatId } = useParams<{ chatId: string }>();

  const [showPopup, setShowPopup] = useState(false);
  const [editingChatId, setEditingChatId] = useState<string | null>(null);
  const [editTitle, setEditTitle] = useState<string>("");
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [menuChatId, setMenuChatId] = useState<string | null>(null);
  const [menuChatTitle, setMenuChatTitle] = useState<string>("");
  const [isSidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() =>
    JSON.parse(localStorage.getItem("sidebarCollapsed") || "false")
  );
  const avatarAnchorRef = useRef<HTMLDivElement>(null);
  const [dbMenuAnchorEl, setDbMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [showAvatarMenu, setShowAvatarMenu] = useState(false);
  const [menuChat, setMenuChat] = useState<ChatSummary | null>(null);
  const [savedChats, setSavedChats] = useState<ChatSummary[]>([]);
  const [messagesMap, setMessagesMap] = useState<MessagesMap>({});
  const [chatId, setChatId] = useState<string>(paramChatId || "");
  const [isChatsLoaded, setIsChatsLoaded] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => location.state?.justLoggedIn || false);

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(isSidebarCollapsed));
  }, [isSidebarCollapsed]);

useEffect(() => {
  if (!userId) return;
    console.log("Вызов очистки кэша при загрузке страницы...");

  let isMounted = true;

  const fetchInitialData = async () => {
    try {
      const apiChats = await getChatsForUser(userId);
      if (!isMounted) return;

      const converted = apiChats.map(mapApiChat);
      const chats = converted.map(c => c.summary);
      const map: { [key: string]: typeof converted[0]['messages'] } = {};
      converted.forEach(c => {
        map[c.summary.chatId] = c.messages;
      });

      setSavedChats(chats);
      setMessagesMap(map);
      setIsChatsLoaded(true);

      // Перенаправляем, только если в URL нет ID чата и список чатов не пуст
      if (!paramChatId && chats.length > 0) {
        navigate(`/chat/${chats[0].chatId}`, { replace: true });
      }
    } catch (error) {
      console.error("Failed to fetch chats:", error);
      setIsChatsLoaded(true); // Устанавливаем в true даже при ошибке, чтобы убрать загрузчик
    }
  };

  fetchInitialData();

  return () => {
    isMounted = false;
  };
}, [userId, paramChatId]); // Убираем navigate и добавляем paramChatId

useEffect(() => {
  // Ничего не делаем, пока чаты не загружены
  if (!isChatsLoaded) return;

  if (paramChatId) {
    const chatExists = savedChats.some(c => c.chatId === paramChatId);
    if (chatExists) {
      // Если чат существует, обновляем внутреннее состояние chatId
      setChatId(paramChatId);
    } else {
      // Если чат не найден (а чаты уже загружены), перенаправляем
      if (savedChats.length > 0) {
        navigate(`/chat/${savedChats[0].chatId}`, { replace: true });
      } else {
        // Если чатов нет, переходим на главную страницу чата
        navigate(`/chat`, { replace: true });
      }
    }
  }
}, [paramChatId, savedChats, isChatsLoaded, navigate]); // chatId можно убрать из зависимостей



  const currentChat = savedChats.find((c) => c.chatId === chatId) || null;
  const numericChatId = currentChat?.id ?? 0;
  const externalId = chatId;
  const targetDbId = currentChat?.dbId ?? "self";
  const chatUniqueId = currentChat?.chatId ?? "unknown";
  const defaultDB = DBS.find((d) => d.id === "self")!;

  const {
    input,
    setInput,
    messages,
    isLoading,
    sendMessage,
    pushMessage
  } = useChatMessages(
    numericChatId,
    externalId,
    messagesMap,
    setMessagesMap,
    savedChats,
    setSavedChats,
    targetDbId,
    chatUniqueId
  );

  const handleSelectChat = useCallback((id: string) => {
    if (id === chatId) return;
    setShowWelcome(false);
    navigate(`/chat/${id}`);
  }, [navigate, chatId]);

  const handleSelectDatabase = useCallback(
    async (db: DatabaseInfo) => {
      if (!userId) return;
      const extId = crypto.randomUUID();
      const api = await createChat(extId, userId, db.name);
      const summary: ChatSummary = {
        id: api.id,
        chatId: api.external_id,
        title: api.title || db.name,
        dbId: db.id,
        dbName: db.name,
      };
      setSavedChats((prev) => [summary, ...prev]);
      setMessagesMap((prev) => ({ ...prev, [summary.chatId]: [] }));
      navigate(`/chat/${summary.chatId}`, { replace: true });
    },
    [userId, navigate]
  );

  const handleRename = async (id: string) => {
    const chat = savedChats.find((c) => c.chatId === id);
    if (!chat) return;
    const updated = savedChats.map((chat) =>
      chat.chatId === id ? { ...chat, title: editTitle } : chat
    );
    setSavedChats(updated);
    localStorage.setItem("savedChats", JSON.stringify(updated));
    setEditingChatId(null);
    try {
      await renameChat(chat.id, editTitle);
    } catch (err) {
      alert("Erorr: " + (err as Error).message);
    }
  };

  const toggleTable = useCallback(
    (msgId: string) => {
      setMessagesMap(prev => ({
        ...prev,
        [chatId]: prev[chatId].map(m =>
          m.id === msgId ? { ...m, isTableExpanded: !m.isTableExpanded } : m
        ),
      }));
    },
    [chatId]
  );

  const handleSend = useCallback(async () => {
    if (!chatId) {
      if (defaultDB && handleSelectDatabase) {
        await handleSelectDatabase(defaultDB);
      }
    } else {
      if (sendMessage) {
        await sendMessage();
      }
    }
  }, [chatId, defaultDB, handleSelectDatabase, sendMessage]);

  const handleMenuOpen = (
    e: React.MouseEvent<HTMLElement>,
    chat: ChatSummary
  ) => {
    setMenuAnchorEl(e.currentTarget);
    setMenuChatId(chat.chatId);
    setMenuChatTitle(chat.title);
    setMenuChat(chat);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setMenuChatId(null);
    setMenuChatTitle("");
  };

  const handleLogout = () => {
    localStorage.clear();
    navigate("/");
  };

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => !prev);
  };

  const editingChatHandlers = {
    setEditTitle,
    handleRename,
    setEditingChatId,
  };

  const handleMenuRename = () => {
    handleMenuClose();
    setTimeout(() => {
      setEditTitle(menuChatTitle!);
      setEditingChatId(menuChatId!);
    }, 0);
  };

  return (
    <>
      <Backdrop
        open={!isChatsLoaded}
        sx={{
          color: '#2196f3',
          zIndex: theme.zIndex.drawer + 10,
          backdropFilter: 'blur(2px)',
          backgroundColor: 'rgba(0,0,0,0.15)'
        }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <div style={{ display: "flex", height: "100vh" }}>
        <ChatSidebar
          username={username ?? "Anon"}
          avatar={null}
          email={Email ?? ""}
          avatarAnchorRef={avatarAnchorRef}
          showAvatarSettings={showAvatarMenu}
          setShowAvatarSettings={setShowAvatarMenu}
          onLogout={handleLogout}
          fullName={fullName ?? "Anon"}
          savedChats={savedChats}
          chatId={chatId}
          editingChatId={editingChatId}
          editTitle={editTitle}
          editingChatHandlers={editingChatHandlers}
          menuAnchorEl={menuAnchorEl}
          menuChatId={menuChatId}
          menuChatTitle={menuChatTitle}
          handleMenuOpen={handleMenuOpen}
          handleMenuClose={handleMenuClose}
          handleMenuRename={handleMenuRename}
          handleMenuDelete={() => { }}
          settingsOpen={isSettingsOpen}
          setSettingsOpen={setSettingsOpen}
          availableDatabases={DBS}
          dbMenuAnchorEl={dbMenuAnchorEl}
          handleDbMenuOpen={(e: { currentTarget: React.SetStateAction<HTMLElement | null>; }) => setDbMenuAnchorEl(e.currentTarget)}
          handleDbMenuClose={() => setDbMenuAnchorEl(null)}
          handleSelectDatabase={handleSelectDatabase}
          handleSelectChat={handleSelectChat}
          setSavedChats={setSavedChats}
          setMessagesMap={setMessagesMap}
          isCollapsed={isSidebarCollapsed}
          toggleSidebar={toggleSidebar}
          navigate={navigate}
        />

        <SidebarHandle collapsed={isSidebarCollapsed} onClick={toggleSidebar}>
          <div
            style={{
              transform: `rotate(${isSidebarCollapsed ? 0 : 180}deg)`,
              transition: 'transform 0.3s ease',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <ChevronRightIcon />
          </div>
        </SidebarHandle>

        <ChatMain isSidebarCollapsed={isSidebarCollapsed}>
          {isChatsLoaded && savedChats.length === 0 ? (
            <EmptyState>
              <EmptyTitle variant="h5">Inizia una chat</EmptyTitle>
              <EmptySubtitle variant="body2">
                Database selezionato: <strong>{defaultDB.name}</strong>
              </EmptySubtitle>
              <NewChatButton onClick={() => handleSelectDatabase(defaultDB)}>
                Crea chat
              </NewChatButton>
            </EmptyState>
          ) : (
            <ChatMainView
              chatId={chatId}
              input={input}
              setInput={setInput}
              messages={messages}
              isLoading={isLoading}
              sendMessage={handleSend}
              handleToggleTableExpand={toggleTable}
              isSidebarCollapsed={isSidebarCollapsed}
              toggleSidebar={toggleSidebar}
              showPopup={showPopup}
              setShowPopup={setShowPopup}
            />
          )}
        </ChatMain>
      </div>
    </>
  );
};

export default Chat;
